import React, { useState, useEffect, useRef } from "react";

import { RichTextModule } from "components/RichTextModule";
import { ContentModuleBaseAttributes } from "utils/orm";
import {
    VideoInfo,
    getJWPlayerVideoInfo,
    initJWPlayerVideo,
    initJWPlayerTracking,
} from "utils/jwPlayer";

export interface JWVideoBlock extends ContentModuleBaseAttributes {
    jwVideo: string;
    richText?: string;
    autostart?: boolean;
    position?: string;
}

const videoInit = async (
    mediaId,
    ref,
    autostart:
    boolean = false,
    setVideoInfo: (args: VideoInfo) => void,
    // eslint-disable-next-line no-undef
    setPlayerInstance: (args: jwplayer.JWPlayer) => void
) => {
    const videoInfo = await getJWPlayerVideoInfo(mediaId);

    setVideoInfo(videoInfo);

    if (videoInfo !== null) {
        const instance = initJWPlayerVideo({
            videoInfo,
            autostart,
            container: ref,
            mute: !!autostart,
            generateSEOMetadata: false // We don't seem to use this
        });
        setPlayerInstance(instance);
    }
};

export const JWVideoModule: React.FC<JWVideoBlock> = ({
    jwVideo,
    richText,
    heading,
    autostart = false,
    position = "post_individual_body"
}) => {
    const jwPlayerContainer = useRef(null);
    const jwPlayerWrapper = useRef(null);
    const [videoInfo, setVideoInfo] = useState(null);
    const [playerInstance, setPlayerInstance] = useState(null);
    const [trackingProps, setTrackingProps] = useState(null);

    // Get the video info, then init the player instance.
    useEffect(() => {
        videoInit(jwVideo, jwPlayerContainer, autostart, setVideoInfo, setPlayerInstance);
    }, []);

    // Init tracking props if the player was successfully instantiated.
    useEffect(() => {
        if ((videoInfo && playerInstance)) {
            setTrackingProps({
                "data-tracking-props": JSON.stringify({
                    sp_impression_component: "insert",
                    sp_impression_insert_type: "media",
                    sp_impression_position: "in_article",
                    sp_media_type: "video",
                    sp_media_name: videoInfo?.title || null,
                    sp_media_provider_key: jwVideo || null,
                    sp_media_provider_name: "jwplayer",
                })
            });
        }
    }, [videoInfo, playerInstance]);

    // Init tracking if tracking props are available.
    useEffect(() => {
        if (trackingProps) {
            initJWPlayerTracking({
                playerInstance,
                videoInfo,
                position,
                element: jwPlayerWrapper?.current
            });
        }
    }, [trackingProps]);

    return (
        <div className="jwplayerModule" {...trackingProps} ref={jwPlayerWrapper}>
            {heading && <h1>{heading}</h1>}
            <div
                id={`jwplayer-${jwVideo}`}
                className="jwplayer-container"
                ref={jwPlayerContainer}
            />
            {richText && <RichTextModule type="richTextBlock" richText={richText} />}
        </div>
    );
};
